import { CreatePoll } from '../components/CreatePoll';
import { seatFacts, seatLayoutFor } from '@anjabom/core';
import { layoutContextKey } from '@anjabom/core';
import { OfficialLayoutStatus } from '../components/OfficialLayoutStatus';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  buildCandidateDecisionFacts,
  buildBookingQuestions,
  buildDecisionShareText,
  createSeatDecision,
  promoteFallback,
  type CandidateEvidenceSnapshot,
  type EvidenceTolerance,
  type LocalReview,
  type SeatCandidate,
  type SeatDecisionRecord,
} from '@anjabom/core';
import { Link, useLocation } from 'react-router-dom';
import { EvidenceDistanceBadge } from '../components/EvidenceDistanceBadge';
import { SurfaceBanner } from '../components/SurfaceBanner';
import { TurnstileChallenge } from '../components/TurnstileChallenge';
import { track } from '../lib/analytics';
import { CompareEvidenceError, loadCompareEvidence, type RemoteEvidenceRow } from '../lib/compare-evidence';
import { buildCandidateEvidenceSnapshot } from '../lib/candidate-evidence';
import { syncSeatDecision } from '../lib/decision-sync';
import { markCompareCompleted, readSurfaceState } from '../lib/surface-state';
import { useLocalState } from '../state/LocalStateProvider';

const rows = ['같은 자리·근처·같은 구역', '실제 낸 돈', '시야', '가림', '소리', '무릎', '의자', '출입', '내 조건 적합'] as const;

const toleranceOptions: Array<{ value: EvidenceTolerance; label: string; description: string }> = [
  { value: 'exact_only', label: '같은 좌석만', description: '정확히 같은 좌석의 기록이 없으면 보류해요.' },
  { value: 'nearby_ok', label: '가까운 좌석까지', description: '같은 열의 가까운 좌석 근거까지 참고해요.' },
  { value: 'unknown_ok', label: '모름도 선택 가능', description: '근거가 없어도 가격·동선 조건으로 직접 결정해요.' },
];

export function ComparePage() {
  const location = useLocation();
  const { state, storageProblem, removeCandidate, saveSeatDecision } = useLocalState();
  const candidateScope = useMemo(() => {
    const routeState = location.state;
    if (!routeState || typeof routeState !== 'object' || !('candidateIds' in routeState)) return null;
    const ids = routeState.candidateIds;
    return Array.isArray(ids) && ids.length <= 4 && ids.every((id) => typeof id === 'string')
      ? new Set<string>(ids)
      : new Set<string>();
  }, [location.state]);
  const candidates = useMemo(
    () => candidateScope ? state.candidates.filter((candidate) => candidateScope.has(candidate.candidateId)) : state.candidates,
    [candidateScope, state.candidates],
  );
  const [remote, setRemote] = useState<RemoteEvidenceRow[]>([]);
  const [activeLayoutByVenue, setActiveLayoutByVenue] = useState<Record<string, string | null | undefined>>({});
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [evidenceStatus, setEvidenceStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [evidenceError, setEvidenceError] = useState('');
  const [evidenceRetry, setEvidenceRetry] = useState(0);
  const [surface] = useState(readSurfaceState);
  const [evidenceTolerance, setEvidenceTolerance] = useState<EvidenceTolerance>(() => matchingDecision(state.seatDecisions, candidates)?.evidenceTolerance ?? 'nearby_ok');
  const [draft, setDraft] = useState<SeatDecisionRecord | null>(() => matchingDecision(state.seatDecisions, candidates));
  const [undoDraft, setUndoDraft] = useState<SeatDecisionRecord | null>(null);
  const [decisionNote, setDecisionNote] = useState(() => draft?.note ?? '');
  const [includePrice, setIncludePrice] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const draftRevision = useRef(0);
  const candidateSignature = JSON.stringify(candidates);
  const previousCandidates = useRef(candidateSignature);
  const [copyMessage, setCopyMessage] = useState('');
  const markDraftEdited = () => {
    draftRevision.current += 1;
    setSaveMessage('변경한 내용은 아직 저장하지 않았어요. 저장하면 이 기기에 남아요.');
  };
  useLayoutEffect(() => {
    if (previousCandidates.current !== candidateSignature) {
      previousCandidates.current = candidateSignature;
      draftRevision.current += 1;
      setSaveMessage('후보가 바뀌었어요. 결정 내용을 확인하고 다시 저장해 주세요.');
    }
  }, [candidateSignature]);

  useEffect(() => {
    if (storageProblem === null && saveMessage.startsWith('결정을 아직 이 기기에 저장하지 못했어요.')) {
      setSaveMessage('현재 내용을 이 기기에 저장했어요. 같은 기기에서 다시 열어 이어 볼 수 있어요.');
    }
  }, [saveMessage, storageProblem]);

  useEffect(() => {
    if (candidates.length < 2) return;
    const controller = new AbortController();
    const sameContext = candidates.every((candidate) => candidate.venueId === candidates[0]?.venueId && candidate.spaceId === candidates[0]?.spaceId && candidate.layoutVersionId === candidates[0]?.layoutVersionId && candidate.instanceId === candidates[0]?.instanceId);
    const loadEvidence = async () => {
      setEvidenceStatus('loading');
      setEvidenceError('');
      try {
        const values = await loadCompareEvidence({ candidates, sameContext, turnstileToken, signal: controller.signal });
        setRemote(values);
        setTurnstileRequired(false);
        setEvidenceStatus('success');
        markCompareCompleted();
        track('compare_viewed');
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
        setRemote([]);
        const turnstile = error instanceof CompareEvidenceError && error.code === 'E_TURNSTILE';
        setTurnstileRequired(turnstile);
        setEvidenceError(turnstile ? '좌석 근거를 보려면 보안 확인을 마쳐 주세요.' : '좌석 근거를 불러오지 못했어요. 0건으로 계산하지 않았어요.');
        setEvidenceStatus('error');
      }
    };
    void loadEvidence();
    return () => controller.abort();
  }, [candidates, evidenceRetry, turnstileToken]);

  useEffect(() => {
    if (candidates.length < 2) return;
    const controller = new AbortController();
    const contexts = [...new Map(candidates.map((candidate) => [layoutContextKey(candidate), candidate])).values()];
    void Promise.all(contexts.map(async (candidate) => {
      const venueId = candidate.venueId;
      const contextKey = layoutContextKey(candidate);
      try {
        const response = await fetch(`/api/venues/${encodeURIComponent(venueId)}?spaceId=${encodeURIComponent(candidate.spaceId)}`, { signal: controller.signal });
        if (!response.ok) return [contextKey, null] as const;
        const value = await response.json() as { activeLayoutVersion?: string | null };
        return [contextKey, value.activeLayoutVersion ?? null] as const;
      } catch {
        return [contextKey, null] as const;
      }
    })).then((entries) => setActiveLayoutByVenue(Object.fromEntries(entries)));
    return () => controller.abort();
  }, [candidates]);

  useEffect(() => {
    if (!draft) return;
    const ids = new Set(candidates.map((candidate) => candidate.candidateId));
    if (!ids.has(draft.chosenCandidateId)) {
      setDraft(null);
      setSaveMessage('1순위 좌석이 후보에서 빠졌어요. 다시 골라 주세요.');
      return;
    }
    setDraft((current) => current ? {
      ...current,
      candidates,
      fallbackCandidateIds: current.fallbackCandidateIds.filter((id) => ids.has(id)),
    } : null);
  }, [candidates]);

  const reviewsFor = (candidateId: string) => state.reviews.filter((review) => review.candidateId === candidateId);
  const evidenceByCandidate = useMemo(() => Object.fromEntries(candidates.map((candidate, index) => {
    return [candidate.candidateId, buildCandidateEvidenceSnapshot(remote[index]?.evidence, reviewsFor(candidate.candidateId))];
  })), [candidates, remote, state.reviews]);
  const facts = useMemo(() => buildCandidateDecisionFacts(candidates, state.viewingPlan, evidenceByCandidate, activeLayoutByVenue, evidenceTolerance, state.fitProfile), [activeLayoutByVenue, candidates, evidenceByCandidate, evidenceTolerance, state.fitProfile, state.viewingPlan]);
  const changedLayouts = facts.filter((fact) => fact.layoutFreshness === 'changed').length;
  const sameContext = candidates.every((candidate) => candidate.venueId === candidates[0]?.venueId && candidate.spaceId === candidates[0]?.spaceId && candidate.layoutVersionId === candidates[0]?.layoutVersionId && candidate.instanceId === candidates[0]?.instanceId);

  if (candidates.length === 1 && candidates[0]) {
    const candidate = candidates[0];
    return <section className="page narrow stack-lg">
      <div><p className="eyebrow">후보 한 자리</p><h1>확인하거나 다른 자리와 비교해요.</h1><p className="muted">후보에서 빼도 이미 남긴 개인 기록은 지워지지 않아요.</p></div>
      <article className="card stack">
        <h2>{candidate.sectionCode} {candidate.rowLabel}열 {candidate.seatNumber}번</h2>
        <p>{candidate.venueName} · {candidate.spaceName}</p>
        <Link className="button button-primary" to={`/seat/${candidate.candidateId}`}>이 자리 확인하기</Link>
        <button className="button button-secondary" type="button" onClick={() => removeCandidate(candidate.candidateId)}>후보에서 빼기</button>
        {storageProblem ? <p role="alert">후보 변경을 아직 기기에 저장하지 못했어요. 위의 복구를 확인해 주세요.</p> : null}
      </article>
      <Link className="button" to="/start">비교할 자리 더 담기</Link>
      <Link to="/passport">남겨 둔 개인 기억 보기</Link>
    </section>;
  }

  if (candidates.length < 2) {
    return (
      <section className="page narrow stack-lg">
        <SurfaceBanner state={surface} empty="공식 배치에서 좌석을 하나 담으면 그 자리의 위치와 기록을 바로 봐요." />
        <p className="eyebrow">좌석 대조</p>
        <h1>아직 담은 좌석이 없어요.</h1>
        <p className="lede">한 자리만 골라도 공식 위치와 아직 확인하지 못한 정보를 나눠 보여 줘요. 두 자리부터는 같은 표에서 대조해요.</p>
        <Link className="button button-primary" to="/start">좌석 담으러 가기</Link>
      </section>
    );
  }

  if (evidenceStatus !== 'success') {
    return (
      <section className="page narrow stack-lg">
        <p className="eyebrow">후보 {candidates.length}자리 결정</p>
        <h1>{evidenceStatus === 'loading' ? '좌석 근거를 확인하고 있어요.' : '좌석 근거를 아직 비교하지 못했어요.'}</h1>
        {evidenceStatus === 'loading' ? <p className="lede" role="status">공식 배치와 좌석별 기록을 불러오는 중이에요.</p> : <div className="notice" role="alert"><span className="notice-mark">!</span><span>{evidenceError}</span></div>}
        {turnstileRequired ? <TurnstileChallenge onToken={setTurnstileToken} /> : null}
        {evidenceStatus === 'error' && !turnstileRequired ? <button className="button button-primary" type="button" onClick={() => setEvidenceRetry((value) => value + 1)}>좌석 근거 다시 불러오기</button> : null}
        <Link className="button" to="/start">후보 좌석 다시 확인하기</Link>
      </section>
    );
  }

  const choosePrimary = (candidateId: string) => {
    if (draft?.chosenCandidateId === candidateId) return;
    markDraftEdited();
    const fallbackCandidateIds = candidates.map((candidate) => candidate.candidateId).filter((id) => id !== candidateId);
    const next = createSeatDecision(candidates, state.viewingPlan, candidateId, fallbackCandidateIds, [], evidenceTolerance, decisionNote);
    setDraft(next);
    setUndoDraft(null);
  };
  const moveFallback = (candidateId: string, direction: -1 | 1) => {
    const index = draft?.fallbackCandidateIds.indexOf(candidateId) ?? -1;
    if (!draft || index < 0 || index + direction < 0 || index + direction >= draft.fallbackCandidateIds.length) return;
    markDraftEdited();
    setDraft((current) => {
      if (!current) return current;
      const index = current.fallbackCandidateIds.indexOf(candidateId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.fallbackCandidateIds.length) return current;
      const next = [...current.fallbackCandidateIds];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return { ...current, fallbackCandidateIds: next };
    });
  };
  const recoverPrimary = () => {
    if (!draft) return;
    try {
      const promoted = promoteFallback({ ...draft, note: decisionNote });
      markDraftEdited();
      setUndoDraft(draft);
      setDraft(promoted);
      setSaveMessage('다음 대안을 1순위로 올렸어요. 저장하면 새 순서가 남아요.');
    } catch (error) {
      setSaveMessage((error as Error).message);
    }
  };
  const save = async () => {
    if (savingRef.current) return;
    if (!draft) { setSaveMessage('1순위 좌석을 먼저 골라 주세요.'); return; }
    savingRef.current = true;
    const savedRevision = draftRevision.current;
    setSaving(true);
    try {
      const next = { ...draft, evidenceTolerance, note: decisionNote.trim(), savedAt: new Date().toISOString(), lastCheckedAt: new Date().toISOString() };
      if (!saveSeatDecision(next)) {
        setSaveMessage('결정을 아직 이 기기에 저장하지 못했어요. 화면 위의 복구 안내에서 내용을 보관하거나 저장을 다시 시도해 주세요.');
        return;
      }
      setDraft(next);
      setUndoDraft(null);
      setSaveMessage('이 기기에 1순위와 대안 순서를 저장했어요.');
      track('decision_saved');
      const sync = await syncSeatDecision(candidates, next.chosenCandidateId, next.clientOpId);
      if (savedRevision !== draftRevision.current) return;
      if (sync.status === 'synced') setSaveMessage('이 기기에 저장했어요. 서버에는 처음 보낸 결정 기록이 연결돼 있어요.');
      if (sync.reason === 'mixed_context') setSaveMessage('서로 다른 장소·배치가 섞여 이 기기에만 저장했어요. 서버에는 잘못된 한 회차로 합치지 않았어요.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const copy = async () => {
    if (!draft) { setCopyMessage('1순위 좌석을 먼저 골라 주세요.'); return; }
    const text = buildDecisionShareText({ ...draft, note: decisionNote }, includePrice);
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage(includePrice ? '예상 금액을 포함해 복사했어요.' : '좌석 순서만 복사했어요. 예상 금액은 넣지 않았어요.');
    } catch {
      downloadText('anjabom-seat-decision.txt', text);
      setCopyMessage('복사 권한이 없어 텍스트 파일로 받았어요.');
    }
  };

  return (
    <section className="page stack-lg">
      <div>
        <p className="eyebrow">후보 {candidates.length}자리 결정</p>
        <h1>좋아 보이는 자리가 아니라, 확인할 수 있는 조건으로 골라요.</h1>
        <p className="lede">공식 위치·같은 자리와 근처 기록·예상 금액·몸 조건을 비교하고, 1순위와 대안을 남겨요. 근거가 없으면 자동으로 가장 좋은 좌석을 만들지 않아요.</p>
      </div>

      {changedLayouts ? <div className="layout-alert" role="alert"><div><p className="journey-stage">배치 변경 감지</p><h2>{changedLayouts}개 후보의 공식 배치가 바뀌었어요.</h2><p>예전 판단을 새 배치에 옮기지 않아요. 공식 좌석도에서 좌석을 다시 담아 주세요.</p></div><Link className="button button-primary" to="/start">새 배치에서 다시 담기</Link></div> : null}
      {!sameContext ? <div className="notice"><span className="notice-mark">!</span><span>서로 다른 장소나 배치의 후보예요. 근거는 각 좌석별로 따로 조회하고, 결정은 이 기기에만 보관해요.</span></div> : null}
      {turnstileRequired ? <TurnstileChallenge onToken={setTurnstileToken} /> : null}

      <section className="decision-desk stack" aria-labelledby="decision-desk-title">
        <div className="decision-desk-head"><div><p className="journey-stage">결정 기준</p><h2 id="decision-desk-title">근거를 어디까지 받아들일까요?</h2></div><Link className="button button-small" to="/visit-plan#decision-heading">일행·예산 조건 바꾸기</Link></div>
        <div className="choice-grid tolerance-grid">{toleranceOptions.map((option) => <button key={option.value} type="button" className="choice-card" aria-pressed={evidenceTolerance === option.value} onClick={() => { if (option.value === evidenceTolerance) return; markDraftEdited(); setEvidenceTolerance(option.value); setDraft((current) => current ? { ...current, evidenceTolerance: option.value } : null); }}><span className="choice-title">{option.label}</span><span className="choice-description">{option.description}</span></button>)}</div>
        <div className="decision-context"><span>1인 예산 <strong>{state.viewingPlan.budgetPerPerson === null ? '미입력' : `${state.viewingPlan.budgetPerPerson.toLocaleString('ko-KR')}원`}</strong></span><span>인원 <strong>{state.viewingPlan.headCount}명</strong></span><span>붙어 앉기 <strong>{state.viewingPlan.headCount > 1 && state.viewingPlan.connectedSeatsRequired ? '필수' : '선택'}</strong></span><span>좌석 기준 <strong>{state.viewingPlan.seatPriorities.length ? `${state.viewingPlan.seatPriorities.length}개` : '미입력'}</strong></span></div>

        <div className={`decision-candidate-grid candidates-${candidates.length}`}>
          {candidates.map((candidate, index) => {
            const fact = facts[index];
            const isChosen = draft?.chosenCandidateId === candidate.candidateId;
            const fallbackIndex = draft?.fallbackCandidateIds.indexOf(candidate.candidateId) ?? -1;
            return <article className={`decision-candidate${isChosen ? ' decision-candidate-primary' : ''}`} key={candidate.candidateId}>
              <div className="decision-candidate-title"><span className="candidate-index">{String.fromCharCode(65 + index)}</span><div><h3>{candidate.sectionCode} {candidate.rowLabel}열 {candidate.seatNumber}번</h3><p className="micro">{candidate.venueName} · {candidate.spaceName}</p></div></div>
              <p className={`decision-readiness readiness-${fact?.readiness ?? 'check_before_purchase'}`}>{fact?.readiness === 'ready_to_choose' ? '입력한 기준으로 선택 가능' : '구매 전에 다시 확인할 조건 있음'}</p>
              <dl className="decision-axis-ledger">
                <div><dt>공식 배치</dt><dd>{layoutStatement(fact)}</dd></div>
                <div><dt>공개 관람 근거</dt><dd>{evidenceStatement(fact)}</dd></div><div><dt>내 기억</dt><dd>{reviewsFor(candidate.candidateId).length}건 · 공개 근거에 포함하지 않아요</dd></div>
                <div><dt>내 결제 예상</dt><dd>{candidate.expectedPrice === null ? '모름 · 예매처 결제 직전 확인' : `${candidate.expectedPrice.toLocaleString('ko-KR')}원 · 내가 입력한 값`}</dd></div>
                <div><dt>실제 낸 돈</dt><dd>{fact?.priceEvidenceStatement ?? '모름'}</dd></div>
                <div><dt>내 조건 근거</dt><dd>{fact?.fitStatements.join(' ') || '모름'} <Link to="/me/fit">조건 바꾸기</Link></dd></div>
                <div><dt>소진 시 순서</dt><dd>{fallbackStatement(isChosen, fallbackIndex, draft)}</dd></div>
              </dl>
              {fact && fact.facts.length + fact.checks.length > 0 ? <details className="candidate-evidence-details"><summary>전체 근거와 재확인 {fact.facts.length + fact.checks.length}개</summary>{fact.facts.length ? <ul className="fact-list fact-positive">{fact.facts.map((value) => <li key={value}>{value}</li>)}</ul> : null}{fact.checks.length ? <ul className="fact-list fact-check">{fact.checks.map((value) => <li key={value}>{value}</li>)}</ul> : null}</details> : null}
              <div className="decision-candidate-actions">
                <button className={`button ${isChosen ? 'button-primary' : ''}`} type="button" onClick={() => choosePrimary(candidate.candidateId)}>{isChosen ? '현재 1순위' : '1순위로 고르기'}</button>
                {fallbackIndex >= 0 ? <div className="fallback-order" aria-label={`${fallbackIndex + 1}번째 대안 순서`}><span>대안 {fallbackIndex + 1}</span><button type="button" className="button button-small" disabled={fallbackIndex === 0} onClick={() => moveFallback(candidate.candidateId, -1)} aria-label={`${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번 대안 순서 올리기`}>위</button><button type="button" className="button button-small" disabled={fallbackIndex === (draft?.fallbackCandidateIds.length ?? 0) - 1} onClick={() => moveFallback(candidate.candidateId, 1)} aria-label={`${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번 대안 순서 내리기`}>아래</button></div> : null}
              </div>
            </article>;
          })}
        </div>

        {draft ? <div className="decision-brief" aria-live="polite"><div><p className="journey-stage">내 결정</p><h3>{candidateLabel(draft.candidates, draft.chosenCandidateId)}을 1순위로 볼게요.</h3><p>{draft.fallbackCandidateIds.length ? `놓치면 ${draft.fallbackCandidateIds.map((id, index) => `${index + 1}. ${candidateLabel(draft.candidates, id)}`).join(' → ')} 순서로 확인해요.` : '남은 대안이 없어요.'}</p>{draft.unavailableCandidateIds.length ? <p className="micro">놓친 후보 {draft.unavailableCandidateIds.length}개는 순서에서 뺐어요.</p> : null}</div><div className="inline"><button className="button button-primary" type="button" disabled={saving} onClick={() => void save()}>{saving ? '저장 중이에요' : '결정 저장하기'}</button><button className="button" type="button" onClick={recoverPrimary} disabled={!draft.fallbackCandidateIds.length}>1순위를 놓쳤어요</button>{undoDraft ? <button className="button" type="button" onClick={() => { markDraftEdited(); setDraft(undoDraft); setUndoDraft(null); }}>되돌리기</button> : null}</div></div> : <div className="empty-decision"><strong>1순위를 고르면 대안 순서까지 자동으로 시작해요.</strong><p className="muted">앉아봄이 근거 없는 점수로 대신 고르지는 않아요.</p></div>}

        {draft ? <section className="booking-checklist stack" aria-labelledby="booking-check-title">
          <div><h3 id="booking-check-title">예매 전, 이 순서로 확인해요</h3><p className="micro">선택한 순서예요. 좌석 재고와 적합성을 보장하지 않아요. 미확인 조건은 공식 예매처·공연장에 확인해 주세요.</p></div>
          {[draft.chosenCandidateId, ...draft.fallbackCandidateIds].map((id, index) => {
            const candidate = candidates.find((item) => item.candidateId === id);
            const fact = facts.find((item) => item.candidateId === id);
            if (!candidate || !fact) return null;
            const inquiry = buildBookingQuestions(candidate, fact, state.viewingPlan);
            return <details className="booking-check" key={id}>
              <summary><strong>{index === 0 ? '1순위' : `대안 ${index}`} · {candidateLabel(candidates, id)}</strong><span>{layoutStatement(fact)} · {evidenceStatement(fact)}</span></summary>
              <div className="stack">
                <div><h4>아직 확인할 점</h4>{fact.checks.length ? <ul>{fact.checks.map((check) => <li key={check}>{check}</li>)}</ul> : <p>현재 비교 기준에서 추가 미확인은 없어요. 실제 예매 조건은 다시 확인해 주세요.</p>}</div>
                <BookingQuestionText text={inquiry.text} label={`${index === 0 ? '1순위' : `대안 ${index}`} 예매처에 확인할 질문`} />
              </div>
            </details>;
          })}
        </section> : null}

        <label className="field"><span className="field-label">일행과 함께 확인할 점</span><textarea className="textarea" value={decisionNote} onChange={(event) => { if (event.target.value !== decisionNote) markDraftEdited(); setDecisionNote(event.target.value); }} placeholder="예: A가 없으면 B, 연석이 아니면 예매하지 않아요." /></label>
        <div className="share-row"><label className="check-item compact-check"><input type="checkbox" checked={includePrice} onChange={(event) => setIncludePrice(event.target.checked)} /><span><strong>복사할 때 예상 금액도 넣어요</strong><small>기본값은 금액 제외예요. 선택했을 때만 일행에게 함께 복사해요.</small></span></label><button className="button" type="button" onClick={() => void copy()}>일행에게 보낼 결정문 복사</button></div>
        {saveMessage ? <p role="status" className="save-status">{saveMessage}</p> : null}{copyMessage ? <p role="status" className="micro">{copyMessage}</p> : null}
      </section>

      <details className="comparison-details">
        <summary><span><strong>근거 항목을 나란히 보기</strong><small>시야·가림·소리·무릎·의자·출입 값을 확인해요.</small></span></summary>
        <div className="compare-wrap">
          <div className="compare-grid" style={{ '--candidate-count': candidates.length } as CSSProperties}>
            <div className="compare-row"><div className="compare-cell compare-label">항목</div>{candidates.map((candidate, index) => <div className="compare-cell" key={candidate.candidateId}><span className="candidate-index">{String.fromCharCode(65 + index)}</span><h3 style={{ marginTop: 10 }}>{candidate.sectionCode} {candidate.rowLabel}열 {candidate.seatNumber}번</h3><p className="micro">{candidate.venueName}<br />{candidate.spaceName}</p><div className="inline"><Link className="button button-ghost" to={`/seat/${candidate.candidateId}`}>좌석 확인하기</Link><button className="button button-ghost button-danger" type="button" onClick={() => removeCandidate(candidate.candidateId)}>빼기</button></div></div>)}</div>
            {candidates.map((candidate)=>{const layout=seatLayoutFor(candidate.venueId,candidate.spaceId);return layout?<div className="compare-cell" key={`facts:${candidate.candidateId}`}><strong>{candidate.sectionCode} {candidate.rowLabel}열 {candidate.seatNumber}번</strong><p>{seatFacts(layout,candidate.sectionCode,candidate.rowLabel,candidate.seatNumber).lines.slice(1).join(' · ')}</p></div>:null;})}
            {rows.map((row) => <div className="compare-row" key={row}><div className="compare-cell compare-label">{row}</div>{candidates.map((candidate, candidateIndex) => <div className="compare-cell" key={`${candidate.candidateId}-${row}`}>{row === '같은 자리·근처·같은 구역' ? evidenceBadge(evidenceByCandidate[candidate.candidateId]) : row === '실제 낸 돈' ? <span>{facts[candidateIndex]?.priceEvidenceStatement ?? '모름'}</span> : row === '내 조건 적합' ? <span>{facts[candidateIndex]?.fitStatements.join(' ') || '모름'}</span> : dimensionValue(row, reviewsFor(candidate.candidateId), remote[candidateIndex]) ? <strong>{dimensionValue(row, reviewsFor(candidate.candidateId), remote[candidateIndex])}</strong> : <strong>모름</strong>}</div>)}</div>)}
          </div>
        </div>
      </details>

      {[...new Map(candidates.map((candidate) => [layoutContextKey(candidate), candidate])).values()].map((candidate) => <OfficialLayoutStatus key={candidate.candidateId} candidate={candidate} />)}

      <div className="inline"><Link className="button" to="/start">후보 더 담기</Link><Link className="button" to="/visit-plan">일행·도착까지 준비하기</Link><Link className="button" to="/passport">저장한 결정 보기</Link></div>
    <CreatePoll candidates={candidates} showTitle={state.viewingPlan.eventTitle}/></section>
  );
}

function matchingDecision(decisions: SeatDecisionRecord[], candidates: SeatCandidate[]): SeatDecisionRecord | null {
  const ids = new Set(candidates.map((candidate) => candidate.candidateId));
  return decisions.find((decision) => decision.candidates.length === candidates.length && decision.candidates.every((candidate) => ids.has(candidate.candidateId))) ?? null;
}

function candidateLabel(candidates: SeatCandidate[], id: string): string {
  const candidate = candidates.find((item) => item.candidateId === id);
  return candidate ? `${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번` : '좌석';
}

function evidenceBadge(snapshot?: CandidateEvidenceSnapshot) {
  if ((snapshot?.exact ?? 0) > 0) return <EvidenceDistanceBadge tier="exact" count={snapshot?.exact ?? 0} />;
  if ((snapshot?.nearby ?? 0) > 0) return <EvidenceDistanceBadge tier="nearby" count={snapshot?.nearby ?? 0} />;
  if ((snapshot?.section ?? 0) > 0) return <EvidenceDistanceBadge tier="section" count={snapshot?.section ?? 0} />;
  return <EvidenceDistanceBadge tier="unknown" />;
}

function layoutStatement(fact: ReturnType<typeof buildCandidateDecisionFacts>[number] | undefined): string {
  if (fact?.layoutFreshness === 'current') return '현재 공식 배치와 같음';
  if (fact?.layoutFreshness === 'changed') return '배치 변경 · 다시 담기';
  return '현재 배치 미확인';
}

function evidenceStatement(fact: ReturnType<typeof buildCandidateDecisionFacts>[number] | undefined): string {
  if (fact?.evidenceTier === 'exact') return `같은 좌석 ${fact.evidenceCount}건`;
  if (fact?.evidenceTier === 'nearby') return `가까운 좌석 ${fact.evidenceCount}건`;
  if (fact?.evidenceTier === 'section') return `같은 구역 참고 ${fact.evidenceCount}건`;
  return '적용할 관람 근거 모름';
}

function fallbackStatement(isChosen: boolean, fallbackIndex: number, draft: SeatDecisionRecord | null): string {
  if (!draft) return '1순위 선택 전';
  if (isChosen) return draft.fallbackCandidateIds.length ? '현재 1순위 · 소진 시 대안 1로 이동' : '현재 1순위 · 남은 대안 없음';
  if (fallbackIndex >= 0) return `대안 ${fallbackIndex + 1}`;
  return '현재 순서에서 제외';
}

function dimensionValue(row: string, reviews: LocalReview[], remote?: RemoteEvidenceRow): string | null {
  const remoteKey: Record<string, 'view' | 'block' | 'sound' | 'knee' | 'seat' | 'access'> = { 시야: 'view', 가림: 'block', 소리: 'sound', 무릎: 'knee', 의자: 'seat', 출입: 'access' };
  const key = remoteKey[row];
  if (!key) return null;
  const values = [
    ...reviews.flatMap((review) => review.scores[row as keyof typeof review.scores] === null ? [] : [review.scores[row as keyof typeof review.scores] as number]),
    ...(remote?.evidence.evidence ?? []).flatMap((entry) => entry.dimensions?.[key] === null || entry.dimensions?.[key] === undefined ? [] : [entry.dimensions[key] as number]),
  ];
  if (!values.length) return null;
  return `${(values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1)}/3 (${values.length}건)`;
}

function downloadText(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function BookingQuestionText({ text, label }: { text: string; label: string }) {
  const [message, setMessage] = useState('');
  const copyQuestions = async () => {
    try { await navigator.clipboard.writeText(text); setMessage('질문을 복사했어요. 관람할 회차를 확인하고 공식 문의창에 붙여 넣어 주세요.'); }
    catch { setMessage('자동으로 복사하지 못했어요. 질문 원문을 눌러 선택한 뒤 직접 복사해 주세요.'); }
  };
  return <div className="stack">
    <label className="field"><span className="field-label">{label}</span><textarea className="textarea" readOnly rows={7} value={text} onFocus={(event) => event.currentTarget.select()} /></label>
    <p className="micro">자동으로 전송하지 않아요. 개인 몸 조건과 결제 내역은 넣지 않았어요.</p>
    <button className="button" type="button" onClick={() => void copyQuestions()}>이 좌석 질문 복사</button>
    {message ? <p role="status">{message}</p> : null}
  </div>;
}
